import { type FieldValue, parseFieldInput } from '@fundline/shared/fields/values';
import { formatPhone, normalizePhone } from '@fundline/shared/phone';
import { NewMerchantSchema } from '@fundline/shared/schemas/merchants';
import { Link, useNavigate } from '@tanstack/react-router';
import { type FormEvent, useState } from 'react';
import { Alert } from '../../components/ui/Alert.tsx';
import { Button } from '../../components/ui/Button.tsx';
import { SelectField, TextAreaField, TextField } from '../../components/ui/Field.tsx';
import { Card, PageHeader, Spinner } from '../../components/ui/Layout.tsx';
import { useWorkspaceClaims } from '../../lib/auth.tsx';
import { consentCapturedAt, localIsoDate } from '../compliance/consent-date.ts';
import { CONSENT_SOURCES } from '../compliance/use-consent.ts';
import { useMembersQuery } from '../members/use-members.ts';
import { type FieldDefinition, useFieldDefinitionsQuery } from '../settings/use-fields.ts';
import { CreateMerchantError, findMerchantIdByPhone, useCreateMerchant } from './use-merchants.ts';

/** The built-in fields asked for on the form; everything else is filled in on the profile. */
const FORM_FIELDS = ['business_name', 'owner_name', 'email', 'state'] as const;

export function NewMerchantPage() {
  const { workspaceId } = useWorkspaceClaims();
  const fields = useFieldDefinitionsQuery(workspaceId);
  const members = useMembersQuery(workspaceId);
  return (
    <div className="flex flex-col gap-5">
      <PageHeader
        title="New merchant"
        description="The phone number identifies the merchant; each number can belong to one merchant only."
      />
      {fields.isPending || members.isPending ? <Spinner /> : null}
      {fields.isError ? <Alert tone="error">{fields.error.message}</Alert> : null}
      {members.isError ? <Alert tone="error">{members.error.message}</Alert> : null}
      {fields.data && members.data ? (
        <NewMerchantForm
          fields={fields.data}
          assignees={members.data
            .filter((m) => m.status === 'active')
            .map((m) => ({ value: m.userId, label: `${m.fullName || m.email} (${m.role})` }))}
        />
      ) : null}
    </div>
  );
}

function NewMerchantForm({
  fields,
  assignees,
}: {
  fields: FieldDefinition[];
  assignees: Array<{ value: string; label: string }>;
}) {
  const { workspaceId, userId, role } = useWorkspaceClaims();
  const navigate = useNavigate();
  const create = useCreateMerchant(workspaceId);
  const [phone, setPhone] = useState('');
  const [assignee, setAssignee] = useState(userId);
  const [inputs, setInputs] = useState<Record<string, string>>({});
  const [errors, setErrors] = useState<Record<string, string>>({});
  // M8 (owner decision 2026-10-14): consent is recorded with every new merchant, and the source is
  // chosen by the person: nothing is preselected and the form cannot be sent without it.
  const [consentSource, setConsentSource] = useState('');
  const [consentDetail, setConsentDetail] = useState('');
  const [consentScope, setConsentScope] = useState('');
  const [consentDate, setConsentDate] = useState(() => localIsoDate(new Date()));
  const [consentNote, setConsentNote] = useState('');
  const [existingId, setExistingId] = useState<string | null>(null);

  const formFields = FORM_FIELDS.flatMap((key) => {
    const f = fields.find((x) => x.systemKey === key && !x.archivedAt);
    return f ? [f] : [];
  });
  const preview = phone.trim() ? normalizePhone(phone) : null;

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    const next: Record<string, string> = {};
    const base = NewMerchantSchema.safeParse({
      phone,
      assignedRepId: role === 'admin' ? assignee : userId,
    });
    if (!base.success) {
      for (const issue of base.error.issues) next[String(issue.path[0])] = issue.message;
    }
    if (!consentSource) next.consentSource = 'Choose how this merchant gave consent';
    if (!consentScope.trim()) next.consentScope = 'Say what the merchant agreed to';
    const captured = consentCapturedAt(consentDate, new Date());
    if (!captured.ok) next.consentDate = captured.error;
    const values: Record<string, FieldValue> = {};
    for (const f of formFields) {
      const r = parseFieldInput(f, inputs[f.id] ?? '');
      if (!r.ok) next[f.id] = r.error;
      else if (r.value !== null) values[f.id] = r.value;
    }
    setErrors(next);
    setExistingId(null);
    if (!base.success || !captured.ok || Object.keys(next).length > 0) return;
    try {
      const id = await create.mutateAsync({
        ...base.data,
        values,
        consent: {
          source: consentSource,
          sourceDetail: consentDetail,
          scope: consentScope,
          capturedAt: captured.capturedAt.toISOString(),
          note: consentNote,
        },
      });
      await navigate({ to: '/merchants/$merchantId', params: { merchantId: id } });
    } catch (err) {
      // Shown below from create.error. Admins get a link to the merchant that has the number.
      if (err instanceof CreateMerchantError && err.code === '23505' && role === 'admin') {
        setExistingId(await findMerchantIdByPhone(workspaceId, base.data.phone));
      }
    }
  }

  return (
    <Card>
      <form className="flex flex-col gap-4" onSubmit={(e) => void onSubmit(e)} noValidate>
        <TextField
          label="Phone"
          type="tel"
          inputMode="tel"
          autoComplete="off"
          value={phone}
          onChange={(e) => setPhone(e.target.value)}
          error={errors.phone}
          hint={
            preview?.ok
              ? `Saved as ${formatPhone(preview.e164)}`
              : 'US numbers only: 10 digits, area code first.'
          }
        />
        {formFields.map((f) =>
          f.type === 'dropdown' ? (
            <SelectField
              key={f.id}
              label={f.label}
              value={inputs[f.id] ?? ''}
              onChange={(e) => setInputs({ ...inputs, [f.id]: e.target.value })}
              options={[
                { value: '', label: 'Not set' },
                ...f.options.map((o) => ({ value: o, label: o })),
              ]}
              error={errors[f.id]}
            />
          ) : (
            <TextField
              key={f.id}
              label={f.label}
              type={f.systemKey === 'email' ? 'email' : 'text'}
              value={inputs[f.id] ?? ''}
              onChange={(e) => setInputs({ ...inputs, [f.id]: e.target.value })}
              error={errors[f.id]}
            />
          ),
        )}
        {role === 'admin' ? (
          <SelectField
            label="Assigned to"
            value={assignee}
            onChange={(e) => setAssignee(e.target.value)}
            options={assignees}
            error={errors.assignedRepId}
          />
        ) : (
          <p className="callout text-ink-muted">This merchant will be assigned to you.</p>
        )}
        <fieldset className="flex flex-col gap-3 rounded-lg p-3 ring-1 ring-hairline">
          <legend className="callout px-1 font-semibold text-ink">Consent</legend>
          <p className="footnote text-ink-muted">
            Every merchant needs a consent record. Choose how this one agreed to texts.
          </p>
          <SelectField
            label="How was consent given?"
            value={consentSource}
            onChange={(e) => setConsentSource(e.target.value)}
            options={[{ value: '', label: 'Choose one' }, ...CONSENT_SOURCES]}
            error={errors.consentSource}
            data-testid="consent-source"
          />
          <TextField
            label="Details (optional)"
            hint="The form, vendor or person, e.g. a referral from Jo at Acme"
            value={consentDetail}
            onChange={(e) => setConsentDetail(e.target.value)}
            maxLength={300}
          />
          <TextField
            label="What they agreed to"
            placeholder="Texts about business funding"
            value={consentScope}
            onChange={(e) => setConsentScope(e.target.value)}
            error={errors.consentScope}
            maxLength={500}
          />
          <TextField
            label="Date"
            type="date"
            value={consentDate}
            max={localIsoDate(new Date())}
            onChange={(e) => setConsentDate(e.target.value)}
            error={errors.consentDate}
          />
          <TextAreaField
            label="Note (optional)"
            value={consentNote}
            onChange={(e) => setConsentNote(e.target.value)}
            maxLength={1000}
            rows={2}
          />
        </fieldset>
        {create.isError ? (
          <Alert tone="error">
            {create.error.message}
            {existingId ? (
              <>
                {' '}
                <Link
                  to="/merchants/$merchantId"
                  params={{ merchantId: existingId }}
                  className="font-semibold text-brand-text underline"
                >
                  Open that merchant
                </Link>
              </>
            ) : null}
          </Alert>
        ) : null}
        <div className="flex gap-2">
          <Button type="submit" busy={create.isPending} disabled={!consentSource}>
            Create merchant
          </Button>
          <Button variant="ghost" onClick={() => void navigate({ to: '/merchants' })}>
            Cancel
          </Button>
        </div>
      </form>
    </Card>
  );
}
